import { Helmet } from "react-helmet-async";
import { SEO_CONFIG, formatSeoTitle, getFullUrl } from "@shared/config/seo.config";
import type { Product } from "@shared/types/product";
import type { Category } from "@shared/types/category";

export interface SeoHeadProps {
  // Meta básicos
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  
  // URLs
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
  
  // Open Graph
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "product";
  ogUrl?: string;
  
  // Twitter Card
  twitterCard?: "summary" | "summary_large_image" | "app" | "player";
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  
  // Schema.org JSON-LD
  schemaJsonLd?: object | object[];
  
  // Producto específico (para generar automáticamente)
  product?: Product;
  
  // Categoría específica (para generar automáticamente)
  category?: Category;
}

/**
 * Componente reutilizable para manejar SEO en todas las páginas
 * Usa react-helmet para inyectar meta tags en el <head>
 */
export function SeoHead({
  title,
  description,
  keywords,
  author,
  canonical,
  noindex = false,
  nofollow = false,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  ogUrl,
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  twitterImage,
  schemaJsonLd,
  product,
  category,
}: SeoHeadProps) {
  // Determinar valores finales
  const finalTitle = formatSeoTitle(
    title || 
    product?.seoTitle || 
    category?.seoTitle || 
    SEO_CONFIG.defaults.title
  );
  
  const finalDescription = 
    description || 
    product?.seoDescription || 
    category?.seoDescription || 
    SEO_CONFIG.defaults.description;
  
  const finalKeywords = 
    keywords || 
    product?.seoKeywords || 
    category?.seoKeywords || 
    SEO_CONFIG.defaults.keywords;
  
  // Imagen para OG y Twitter
  const getImageUrl = (img: string | { url?: string } | null | undefined): string => {
    if (!img) return SEO_CONFIG.images.defaultOgImage;
    if (typeof img === 'string') return img;
    return img.url || SEO_CONFIG.images.defaultOgImage;
  };
  
  const defaultImage = product 
    ? getImageUrl(product.primaryImage || product.images?.[0])
    : category?.image || SEO_CONFIG.images.defaultOgImage;
  
  const finalOgImage = ogImage || defaultImage;
  const finalOgTitle = ogTitle || finalTitle;
  const finalOgDescription = ogDescription || finalDescription;
  const finalOgUrl = ogUrl || canonical || getFullUrl();
  
  // Twitter
  const finalTwitterTitle = twitterTitle || finalTitle;
  const finalTwitterDescription = twitterDescription || finalDescription;
  const finalTwitterImage = twitterImage || finalOgImage;
  
  // Canonical URL
  const finalCanonical = canonical ? getFullUrl(canonical) : getFullUrl();
  
  // Robots meta
  const robotsContent = [
    noindex ? "noindex" : "index",
    nofollow ? "nofollow" : "follow",
  ].join(", ");
  
  // Schema.org JSON-LD
  const schemaArray = Array.isArray(schemaJsonLd) 
    ? schemaJsonLd 
    : schemaJsonLd 
      ? [schemaJsonLd] 
      : [];
  
  return (
    <Helmet>
      {/* Meta básicos */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="author" content={author || SEO_CONFIG.defaults.author} />
      <meta name="robots" content={robotsContent} />
      <link rel="canonical" href={finalCanonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:site_name" content={SEO_CONFIG.defaults.siteName} />
      <meta property="og:locale" content={SEO_CONFIG.defaults.locale} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
      
      {/* Schema.org JSON-LD */}
      {schemaArray.map((schema, index) => (
        <script
          key={index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </Helmet>
  );
}

